'use client';
import { animated, config, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import constant from 'lodash/constant';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';
import {
  type ComponentType,
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Portal } from '@air/primitive-portal';

import { ToastItem, type ToastItemProps } from './ToastItem';

export type ToastItemPropsType = Omit<ToastItemProps, 'children' | 'onDismiss'>;

/**
 * Minimal props interface for custom toast containers.
 * Any component that accepts these props can be used as a CustomContainer.
 */
export type CustomToastContainerProps = {
  children: ReactNode;
  onDismiss: () => void;
  timeBeforeAutoDismiss?: number | null;
  type?: 'assertive' | 'polite';
};

export type ToastOptions =
  | ({ onDismiss?: () => void } & ToastItemPropsType & { CustomContainer?: never })
  | ({ onDismiss?: () => void } & { CustomContainer: ComponentType<CustomToastContainerProps> } & Omit<
        CustomToastContainerProps,
        'children' | 'onDismiss'
      >);

export type Toast = {
  id: string;
  content: (({ onDismiss }: { onDismiss: () => void }) => ReactNode) | ReactNode;
  options?: ToastOptions;
};

const createToast = (content: Toast['content'], options?: ToastOptions): Toast => ({
  content,
  id: uniqueId('toast'),
  options,
});

const toastOptionDefaults: ToastOptions = {
  color: 'black',
  onDismiss: noop,
  prefix: null,
  suffix: null,
  timeBeforeAutoDismiss: 5_000,
  type: 'polite',
  withCloseButton: false,
};

type ToastContextValue = {
  showToast: (content: Toast['content'], options?: ToastOptions) => void;
  dismissAllToasts: () => void;
  setToastBottomOffset: (offset: number) => void;
  setToastXOffset: (offset: number) => void;
  getToastXOffset: () => number;
  setToastRightOffset: (offset: number) => void;
  getToastRightOffset: () => number;
  setToastContainer: (el: HTMLElement | null) => void;
};

const defaultValue: ToastContextValue = {
  dismissAllToasts: noop,
  getToastRightOffset: constant(0),
  getToastXOffset: constant(0),
  setToastBottomOffset: noop,
  setToastContainer: noop,
  setToastRightOffset: noop,
  setToastXOffset: noop,
  showToast: noop,
};

const ToastContext = createContext(defaultValue);

export type ToastProviderProps = {
  children: ReactNode;
  /**
   * When provided, toasts render inside this container with absolute positioning
   * instead of rendering to document.body with fixed positioning.
   * The container element should have `position: relative`.
   */
  container?: HTMLElement | null;
  /**
   * Allows you to customize the rendering of the toast.
   */
  renderToast?: (
    toast: ToastOptions & {
      children?: ReactNode;
    },
  ) => ReactNode;
};

export const ToastProvider = memo(({ children, container: containerProp, renderToast }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [bottomOffset, setToastBottomOffset] = useState<number>(0);
  const [internalContainer, setToastContainer] = useState<HTMLElement | null>(null);
  const toastXOffsetRef = useRef<number>(0);
  const toastRightOffsetRef = useRef<number>(0);
  const [toastXOffset, setToastXOffsetState] = useState<number>(0);
  const [toastRightOffset, setToastRightOffsetState] = useState<number>(0);
  const container = internalContainer ?? containerProp;
  const dismissAllToasts = useCallback(() => {
    // do not set an empty array if there are no toast to avoid unnecessary rerenders on route change
    setToasts((currentToasts) => (currentToasts.length === 0 ? currentToasts : []));
  }, []);

  const clearToast = (id: string) => setToasts((current) => current.filter((t) => t.id !== id));

  const value: ToastContextValue = useMemo(
    () => ({
      dismissAllToasts,
      getToastRightOffset: () => toastRightOffsetRef.current,
      getToastXOffset: () => toastXOffsetRef.current,
      setToastBottomOffset,
      setToastContainer,
      setToastRightOffset: (offset: number) => {
        toastRightOffsetRef.current = offset;
        setToastRightOffsetState(offset);
      },
      setToastXOffset: (offset: number) => {
        toastXOffsetRef.current = offset;
        setToastXOffsetState(offset);
      },
      showToast: (content, options) => {
        setToasts((currentToasts) => [...currentToasts, createToast(content, options)]);
      },
    }),
    [dismissAllToasts],
  );

  const transitions = useTransition(toasts, {
    config: config.stiff,
    enter: {
      opacity: 1,
      y: 0,
    },
    from: {
      opacity: 0,
      y: 16,
    },
    keys: (item) => item.id,
    leave: {
      opacity: 0,
      y: 16,
    },
  });

  return (
    <ToastContext.Provider value={value}>
      {children}
      {!!toasts.length && (
        <Portal container={container}>
          <ul
            className={classNames(
              container
                ? 'pointer-events-none absolute right-0 bottom-0 left-0 z-portal m-0 flex flex-col items-center px-0 py-3'
                : 'pointer-events-none fixed bottom-0 z-portal m-0 flex flex-col items-center px-0 py-3',
            )}
            data-testid="TOAST_LIST"
            style={
              container
                ? { bottom: bottomOffset || undefined }
                : {
                    left: toastXOffset,
                    marginBottom: bottomOffset,
                    right: toastRightOffset,
                  }
            }
          >
            {transitions((style, item) => {
              const {
                color,
                onDismiss,
                prefix,
                suffix,
                timeBeforeAutoDismiss,
                withCloseButton,
                type,
                CustomContainer,
              } = {
                ...toastOptionDefaults,
                ...item.options,
              };

              const dismissHandler = () => {
                onDismiss?.();
                clearToast(item.id);
              };

              const children = isFunction(item.content) ? item.content({ onDismiss: dismissHandler }) : item.content;

              let toastContent: ReactNode;

              if (renderToast) {
                toastContent = renderToast({
                  children,
                  color,
                  onDismiss: dismissHandler,
                  prefix,
                  suffix,
                  timeBeforeAutoDismiss,
                  type,
                  withCloseButton,
                });
              } else if (CustomContainer) {
                toastContent = (
                  <CustomContainer onDismiss={dismissHandler} timeBeforeAutoDismiss={timeBeforeAutoDismiss} type={type}>
                    {children}
                  </CustomContainer>
                );
              } else {
                toastContent = (
                  <ToastItem
                    color={color}
                    onDismiss={dismissHandler}
                    prefix={prefix}
                    suffix={suffix}
                    timeBeforeAutoDismiss={timeBeforeAutoDismiss}
                    type={type}
                    withCloseButton={withCloseButton}
                  >
                    {children}
                  </ToastItem>
                );
              }

              return (
                <animated.li className="pointer-events-auto list-none" style={style}>
                  {toastContent}
                </animated.li>
              );
            })}
          </ul>
        </Portal>
      )}
    </ToastContext.Provider>
  );
});

ToastProvider.displayName = 'ToastProvider';

export const useToasts = () => {
  const context = useContext(ToastContext);

  if (context === defaultValue) {
    throw 'useToasts used outside of ToastProvider';
  }

  return context;
};
